import * as React from "react";
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export interface TimedLineChartProps {
    data: Array<{ name: string; value: number; companyId?: string }>;
    loading: boolean;
    onBarClick?: (payload: any) => void;
    containerId?: string;
    height?: number;
    useLegacyWidth?: boolean;
}

export function TimedBarChart(props: React.PropsWithChildren<TimedLineChartProps>) {

    if(props.loading) {
        return (
            <div style={{ width: "100%", height: "100%" }}>
                <h1>Loading...</h1>
            </div>
        );
    }

    if (props.data.length === 0) {
        return (
            <div style={{ width: "100%", height: "100%" }}>
                <p>No data available for the selected filters.</p>
            </div>
        );
    }

    const chartHeight = props.height || 600;
    const useLegacy = props.useLegacyWidth === true || props.containerId !== undefined;

    if (useLegacy) {
        const containerId = props.containerId || "parent-id";
        const width = document.getElementById(containerId)?.offsetWidth || 800;
        return (
            <div id={containerId} style={{ width: "100%", height: "100%" }}>
                <BarChart
                    width={width}
                    height={chartHeight}
                    data={props.data}
                    layout="vertical"
                >
                    <CartesianGrid stroke="#ccc" />
                    <XAxis type="number" />
                    <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={150}
                        tick={{ fontSize: 12 }}
                    />
                    <Tooltip />
                    <Bar
                        dataKey="value"
                        fill="#8884d8"
                        onClick={(data: any) => {
                            if (props.onBarClick) {
                                props.onBarClick(data?.payload);
                            }
                        }}
                    />
                </BarChart>
            </div>
        );
    }

    // For many companies, use horizontal bars (vertical layout) for better scalability
    const useHorizontalBars = props.data.length > 10;
    
    if (useHorizontalBars) {
        // Calculate dynamic height based on number of companies
        const dynamicHeight = Math.max(400, props.data.length * 30);
        return (
            <div style={{ width: "100%", height: dynamicHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={props.data} layout="vertical">
                        <CartesianGrid stroke="#ccc" />
                        <XAxis type="number" />
                        <YAxis 
                            type="category" 
                            dataKey="name" 
                            width={150}
                            tick={{ fontSize: 12 }}
                        />
                        <Tooltip />
                        <Bar
                            dataKey="value"
                            fill="#8884d8"
                            onClick={(data: any) => {
                                if (props.onBarClick) {
                                    props.onBarClick(data?.payload);
                                }
                            }}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    }
    
    return (
        <div style={{ width: "100%", height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={props.data}>
                    <CartesianGrid stroke="#ccc" />
                    <XAxis 
                        dataKey="name" 
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 11 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar
                        dataKey="value"
                        fill="#8884d8"
                        onClick={(data: any) => {
                            if (props.onBarClick) {
                                props.onBarClick(data?.payload);
                            }
                        }}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}